import { useEffect, useRef } from "react";
import { useGoogleMap } from "@react-google-maps/api";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { PathLayer, ScatterplotLayer } from "@deck.gl/layers";
import { PathStyleExtension } from "@deck.gl/extensions";

import { estimatedRuns } from "../../services/environmentAggregation";

// Spessore della polyline, in pixel: "bella spessa", resta tale a ogni zoom.
export const PATH_WIDTH_PX = 9;
export const SAMPLE_RADIUS_PX = 5;
// Tratti stimati (buchi GPS): base colorata quasi trasparente e tratteggio colorato pieno sopra.
// Il tratteggio e' in multipli dello spessore (10 px di trattino, 8 px di spazio), quindi in pixel.
const ESTIMATED_BASE_PX = 5;
const ESTIMATED_DASH_PX = 4;
const ESTIMATED_DASH = [2.5, 2];
const ALPHA_SOLID = 242; // 0.95
const ALPHA_ESTIMATED = 51; // 0.2
const WHITE = [255, 255, 255];
const DOT_BORDER = [40, 40, 40];

/**
 * Overlay deck.gl sopra la mappa Google per i dati ambientali: una polyline a segmenti già
 * colorati e i pallini dei campioni già colorati. Chi lo usa decide come calcolare colori e
 * posizioni (nel tempo per una sessione singola, nello spazio per la sessione comune). I segmenti
 * e i pallini `estimated` (posizione stimata in un buco GPS) si disegnano tratteggiati e cavi.
 *
 * @param segments [{ path: [[lng, lat], [lng, lat]], color: [r, g, b], estimated? }]
 * @param dots     [{ lat, lng, color: [r, g, b], estimated?, ...dati per il tooltip }]
 * @param bounds   [{ lat, lng }] punti su cui inquadrare la mappa, una volta sola quando arrivano
 * @param getTooltipText (dot) => string | null
 */
export default function EnvironmentOverlay({ segments, dots, bounds, getTooltipText }) {
    const map = useGoogleMap();
    const overlayRef = useRef(null);
    const tooltipRef = useRef(getTooltipText);
    tooltipRef.current = getTooltipText;

    useEffect(() => {
        if (!map) return undefined;
        const overlay = new GoogleMapsOverlay({
            layers: [],
            getTooltip: ({ object }) => {
                if (!object || !tooltipRef.current) return null;
                const text = tooltipRef.current(object);
                return text ? { text } : null;
            },
        });
        overlay.setMap(map);
        overlayRef.current = overlay;
        return () => {
            overlay.setMap(null);
            overlayRef.current = null;
        };
    }, [map]);

    // Inquadra una volta, quando arrivano i punti.
    useEffect(() => {
        if (!map || !bounds || bounds.length === 0) return;
        const box = new window.google.maps.LatLngBounds();
        bounds.forEach(p => box.extend({ lat: p.lat, lng: p.lng }));
        map.fitBounds(box, 48);
    }, [map, bounds]);

    useEffect(() => {
        if (!overlayRef.current) return;
        const layers = [
            // Tratti misurati: linea piena. I tratti stimati: base colorata quasi trasparente, sotto
            // il tratteggio; cosi' la linea pesa poco e il tratteggio resta leggibile.
            new PathLayer({
                id: "environment-path",
                data: segments || [],
                getPath: d => d.path,
                getColor: d => [...d.color, d.estimated ? ALPHA_ESTIMATED : ALPHA_SOLID],
                getWidth: d => (d.estimated ? ESTIMATED_BASE_PX : PATH_WIDTH_PX),
                widthUnits: "pixels",
                capRounded: true,
                jointRounded: true,
            }),
            // Tratteggio colorato pieno sui tratti stimati, un percorso per tratto continuo con un
            // colore per vertice: nei suoi spazi si vede solo la base trasparente. highPrecisionDash
            // misura il passo lungo tutto il percorso: resta uguale in pixel, quindi allontanando la
            // mappa i trattini diminuiscono. Senza, o con dashJustified, il passo riparte a ogni
            // segmento e i segmenti da 15 m, piu' corti del passo, diventano una linea piena.
            new PathLayer({
                id: "environment-path-estimated",
                data: estimatedRuns(segments),
                getPath: d => d.path,
                getColor: d => d.colors,
                getWidth: ESTIMATED_DASH_PX,
                widthUnits: "pixels",
                // trattini dritti: con le punte arrotondate gli spazi si chiuderebbero
                capRounded: false,
                jointRounded: true,
                getDashArray: ESTIMATED_DASH,
                extensions: [new PathStyleExtension({ dash: true, highPrecisionDash: true })],
            }),
            new ScatterplotLayer({
                id: "environment-samples",
                data: dots || [],
                getPosition: d => [d.lng, d.lat],
                // pallino stimato: vuoto, col bordo del colore del valore
                getFillColor: d => (d.estimated ? WHITE : d.color),
                getLineColor: d => (d.estimated ? d.color : DOT_BORDER),
                // bordo in pixel: in metri (il default) zoomando molto copriva tutto il pallino di scuro
                lineWidthUnits: "pixels",
                getLineWidth: d => (d.estimated ? 3 : 1),
                stroked: true,
                radiusUnits: "pixels",
                getRadius: SAMPLE_RADIUS_PX,
                pickable: true,
            }),
        ];
        overlayRef.current.setProps({ layers });
    }, [segments, dots]);

    return null;
}
